import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminGslt.css';

/**
 * Admin panel: GSLT (Game Server Login Token) keepalive.
 *
 * Visar per-server-status för Steam game-server-tokens (senaste login + dagar
 * till utgång, läst från Steams Web API) och styr den schemalagda
 * keepalive-funktionen som bootar "stale" servrar för att förnya inloggningen
 * innan tokenen expirar (~30 dagars inaktivitet). Owner-only i Django; alla
 * anrop proxas via FastAPI (`/api/admin/gslt/*`).
 */
export default function AdminGslt({ perms }) { // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const fetchStatus = useCallback(async (refresh = false) => {
    setStatusLoading(true);
    try {
      const url = `${API_URL}/api/admin/gslt/status${refresh ? '?refresh=1' : ''}`;
      const r = await fetch(url, { headers: getAuthHeaders() });
      if (r.ok) {
        setStatus(await r.json());
      }
    } catch {
      // silent — status är best-effort
    } finally {
      setStatusLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/gslt/config`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setConfig(data);
        setForm({
          enabled: !!data.enabled,
          interval_days: data.interval_days,
          threshold_days: data.threshold_days,
          run_time: data.run_time,
        });
      }
    } catch {
      // silent
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
    fetchConfig();
  }, [fetchStatus, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/admin/gslt/config`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || data.detail || t('settings.saveError'));
      } else {
        setConfig(data);
        setNotice(t('admin.gslt.saved'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async (forceAll) => {
    setShowConfirmAll(false);
    setRunning(true);
    setError('');
    setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/admin/gslt/run`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_all: !!forceAll }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || data.detail || t('settings.networkError'));
      } else {
        setNotice(t('admin.gslt.runQueued'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setRunning(false);
    }
  };

  /** Färgklass för "dagar till utgång": röd ≤5/utgången, gul ≤10, annars grön. */
  const expiryClass = (row) => {
    if (!row.token_found || row.is_expired) return 'gslt-badge gslt-badge-danger';
    const d = row.days_until_expiry;
    if (d == null || d <= 5) return 'gslt-badge gslt-badge-danger';
    if (d <= 10) return 'gslt-badge gslt-badge-warn';
    return 'gslt-badge gslt-badge-ok';
  };

  const expiryLabel = (row) => {
    if (!row.token_found) return t('admin.gslt.tokenMissing');
    if (row.is_expired) return t('admin.gslt.expired');
    if (row.days_until_expiry == null) return '—';
    return t('admin.gslt.daysLeft', { count: Math.round(row.days_until_expiry) });
  };

  const fmtDate = (unix) => {
    if (!unix) return t('admin.gslt.never');
    return new Date(unix * 1000).toLocaleString(currentLocale());
  };

  const fmtIso = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(currentLocale());
  };

  const lastResult = config?.last_result;

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.gslt.title')}</h1>

      {error && <div className="admin-error gslt-mb">{error}</div>}
      {notice && <div className="admin-success gslt-mb">{notice}</div>}

      {/* ── Status ───────────────────────────────────────────────── */}
      <div className="admin-card">
        <div className="admin-card-title">{t('admin.gslt.statusTitle')}</div>
        <div className="admin-card-desc">{t('admin.gslt.statusDesc')}</div>

        {statusLoading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : !status ? (
          <div className="admin-info">{t('admin.gslt.noStatus')}</div>
        ) : (
          <>
            {!status.steam_readable && (
              <div className="admin-error gslt-mb">{t('admin.gslt.steamUnreadable')}</div>
            )}
            <div className="gslt-table-wrap">
              <table className="gslt-table">
                <thead>
                  <tr>
                    <th>{t('admin.gslt.colServer')}</th>
                    <th>{t('admin.gslt.colMemo')}</th>
                    <th>{t('admin.gslt.colLastLogin')}</th>
                    <th>{t('admin.gslt.colExpiry')}</th>
                    <th>{t('admin.gslt.colOnline')}</th>
                  </tr>
                </thead>
                <tbody>
                  {status.servers.map((row) => (
                    <tr key={row.server_id}>
                      <td>{row.name}</td>
                      <td>{row.memo || '—'}</td>
                      <td>{fmtDate(row.last_logon)}</td>
                      <td><span className={expiryClass(row)}>{expiryLabel(row)}</span></td>
                      <td>{row.online == null ? '?' : row.online ? '🟢' : '⚪'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {status.orphan_tokens?.length > 0 && (
              <div className="admin-card-desc gslt-mt">
                {t('admin.gslt.orphanNote', { count: status.orphan_tokens.length })}
              </div>
            )}
            <button
              className="admin-btn admin-btn-secondary gslt-mt"
              onClick={() => fetchStatus(true)}
              disabled={statusLoading}
            >
              {t('admin.gslt.refresh')}
            </button>
          </>
        )}
      </div>

      {/* ── Schema ───────────────────────────────────────────────── */}
      <div className="admin-card">
        <div className="admin-card-title">{t('admin.gslt.scheduleTitle')}</div>
        <div className="admin-card-desc">{t('admin.gslt.scheduleDesc')}</div>

        {!form ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : (
          <>
            <div className="admin-row gslt-row">
              <span className="admin-label">{t('admin.gslt.enabled')}</span>
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                <span className="admin-toggle-slider"></span>
              </label>
            </div>

            <div className="admin-row gslt-row">
              <span className="admin-label">{t('admin.gslt.intervalDays')}</span>
              <input
                className="admin-input gslt-num"
                type="number"
                min="1"
                value={form.interval_days}
                onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
              />
            </div>

            <div className="admin-row gslt-row">
              <span className="admin-label">{t('admin.gslt.thresholdDays')}</span>
              <input
                className="admin-input gslt-num"
                type="number"
                min="1"
                value={form.threshold_days}
                onChange={(e) => setForm({ ...form, threshold_days: e.target.value })}
              />
            </div>

            <div className="admin-row gslt-row">
              <span className="admin-label">{t('admin.gslt.runTime')}</span>
              <input
                className="admin-input gslt-num"
                type="time"
                value={form.run_time}
                onChange={(e) => setForm({ ...form, run_time: e.target.value })}
              />
            </div>

            <div className="admin-stat-grid gslt-mt">
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.gslt.nextRun')}</div>
                <div className="admin-stat-value">{fmtIso(config?.next_run)}</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.gslt.lastRun')}</div>
                <div className="admin-stat-value">{fmtIso(config?.last_run)}</div>
              </div>
            </div>

            <button className="admin-btn gslt-mt" onClick={handleSave} disabled={saving}>
              {saving ? t('admin.gslt.saving') : t('admin.gslt.save')}
            </button>
          </>
        )}
      </div>

      {/* ── Kör nu ───────────────────────────────────────────────── */}
      <div className="admin-card">
        <div className="admin-card-title">{t('admin.gslt.runTitle')}</div>
        <div className="admin-card-desc">{t('admin.gslt.runDesc')}</div>

        <div className="gslt-btn-row">
          <button className="admin-btn" onClick={() => handleRun(false)} disabled={running}>
            {t('admin.gslt.runStale')}
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => setShowConfirmAll(true)}
            disabled={running}
          >
            {t('admin.gslt.runAll')}
          </button>
        </div>

        {lastResult && (
          <div className="gslt-result gslt-mt">
            <div className="admin-card-desc">
              {t('admin.gslt.lastResult', {
                refreshed: lastResult.refreshed,
                skipped: lastResult.skipped,
                failed: lastResult.failed,
              })}
            </div>
          </div>
        )}
      </div>

      {showConfirmAll && (
        <div className="admin-confirm-overlay" onClick={() => setShowConfirmAll(false)}>
          <div className="admin-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-card-title">{t('admin.gslt.runAll')}</div>
            <div className="admin-card-desc">{t('admin.gslt.confirmAllText')}</div>
            <div className="admin-confirm-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowConfirmAll(false)}
              >
                {t('admin.gslt.cancel')}
              </button>
              <button className="admin-btn" onClick={() => handleRun(true)}>
                {t('admin.gslt.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
